import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { getLocaleForCurrency } from '@/lib/currencyConfig';

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/** Count-up duration when section scrolls into view */
const COUNT_UP_MS = 1550;

function useCountUp(target, isActive, durationMs = COUNT_UP_MS) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    let raf;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const p = Math.min(1, elapsed / durationMs);
      const eased = easeOutCubic(p);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isActive, target, durationMs]);

  return value;
}

function formatPaidOut(amount, currency) {
  const code = (currency || 'GBP').toUpperCase();
  try {
    return new Intl.NumberFormat(getLocaleForCurrency(code), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
}

function StatCell({ label, target, isCurrency, currency }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.35 });
  const count = useCountUp(target, isInView, COUNT_UP_MS);

  const display = isCurrency ? formatPaidOut(count, currency) : count.toLocaleString('en-GB');
  const watermark = isCurrency ? formatPaidOut(count, currency) : count.toLocaleString('en-GB');

  // Foreground value: container-relative so long strings (e.g. £8,300, 3,168) stay inside the cell.
  const valueClass = isCurrency
    ? 'max-w-full text-center font-black tabular-nums tracking-tight text-white [font-size:min(2.15rem,11cqi)] @[280px]:[font-size:min(2.35rem,12cqi)] sm:[font-size:min(2.5rem,11cqi)] md:[font-size:min(2.75rem,10cqi)] lg:[font-size:min(3rem,9.5cqi)] leading-none'
    : 'max-w-full text-center font-black tabular-nums tracking-tight text-white [font-size:min(2.35rem,13cqi)] @[280px]:[font-size:min(2.55rem,14cqi)] sm:[font-size:min(2.75rem,13cqi)] md:[font-size:min(3rem,12cqi)] lg:[font-size:min(3.25rem,11cqi)] leading-none';

  return (
    <div
      ref={ref}
      className="@container stat-cell relative flex min-h-[5.5rem] w-full min-w-0 flex-col items-center justify-center overflow-hidden rounded-xl px-1.5 py-3 [container-type:inline-size] sm:min-h-[6rem] sm:px-2 sm:py-4 md:min-h-[6.5rem] md:px-2.5"
    >
      {/* Background numerals — stay inside cell, no interaction with layout */}
      <span
        className="pointer-events-none absolute inset-0 flex select-none items-center justify-center overflow-hidden px-0.5"
        aria-hidden
      >
        <span className="max-w-full truncate text-center font-black tabular-nums text-white/[0.06] [font-size:min(4.5rem,28cqi)] leading-none sm:[font-size:min(5.5rem,26cqi)] md:[font-size:min(6rem,24cqi)]">
          {watermark}
        </span>
      </span>

      <p className="relative z-10 w-full min-w-0 px-1 text-center text-[clamp(0.7rem,3.5cqi+0.35rem,0.8125rem)] font-semibold uppercase leading-tight tracking-[0.14em] text-gray-200 sm:text-[clamp(0.72rem,3cqi+0.35rem,0.875rem)] sm:tracking-[0.16em] md:text-[clamp(0.75rem,2.5cqi+0.35rem,0.875rem)] md:tracking-[0.18em]">
        {label}
      </p>
      <p className={`relative z-10 mt-1 w-full min-w-0 px-0.5 ${valueClass}`} aria-live="polite">
        {display}
      </p>
    </div>
  );
}

const STATS = [
  { label: 'Artists', target: 44, isCurrency: false },
  { label: 'Performances', target: 792, isCurrency: false },
  { label: 'Requests', target: 3168, isCurrency: false },
  { label: 'Paid Out', target: 8300, isCurrency: true },
];

/**
 * Landing stats: 2×2 on small screens, single row on md+.
 * Foreground label + value; count-up when scrolled into view.
 */
export default function LandingStatsTicker({ currency = 'GBP' }) {
  return (
    <div className="mx-auto mt-16 max-w-5xl border-t border-white/10 px-2 pt-12 sm:px-4 md:px-0">
      <div className="grid min-w-0 grid-cols-2 gap-x-3 gap-y-4 sm:gap-x-4 sm:gap-y-5 md:grid-cols-4 md:gap-x-5 md:gap-y-0 lg:gap-x-6">
        {STATS.map((s) => (
          <StatCell
            key={s.label}
            label={s.label}
            target={s.target}
            isCurrency={s.isCurrency}
            currency={currency}
          />
        ))}
      </div>
    </div>
  );
}
